import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout | error

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) {
      setStatus("error");
      return;
    }
    let active = true;
    let attempts = 0;
    const poll = async () => {
      if (!active) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
      } catch {
        /* transient — keep polling */
      }
      if (attempts >= 15) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-background text-primary flex items-center justify-center px-6 relative">
      <div className="noise-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-lg bg-white border border-border rounded-sm p-10 sm:p-14"
        data-testid="payment-success-page"
      >
        <Link to="/" className="font-serif text-2xl">CakesbyaKid</Link>

        {status === "checking" && (
          <div className="mt-10" data-testid="payment-checking">
            <Loader2 className="animate-spin text-accent" size={30} />
            <h1 className="font-serif text-3xl sm:text-4xl mt-6">Confirming your deposit…</h1>
            <p className="text-secondary mt-3">One moment while we check with Stripe.</p>
          </div>
        )}

        {status === "paid" && (
          <div className="mt-10" data-testid="payment-paid">
            <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center mb-6">
              <Check className="text-accent-foreground" size={26} />
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl">Your week is reserved!</h1>
            <p className="text-secondary mt-4 leading-relaxed">
              Your <span className="text-primary font-medium">$25 deposit</span> is paid and your cake request is confirmed.
              We'll email you within 48 hours to finalise the details. The remaining balance is due{" "}
              <span className="text-primary font-medium">in cash on pickup</span>.
            </p>
            <Button asChild className="btn-sweep bg-primary text-primary-foreground hover:text-primary-foreground rounded-sm h-12 px-8 mt-8">
              <Link to="/" data-testid="back-home-button">Back to home</Link>
            </Button>
          </div>
        )}

        {(status === "failed" || status === "error" || status === "timeout") && (
          <div className="mt-10" data-testid="payment-issue">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-6">
              <AlertTriangle className="text-accent" size={26} />
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl">
              {status === "timeout" ? "Still processing…" : "We couldn't confirm payment"}
            </h1>
            <p className="text-secondary mt-4 leading-relaxed">
              {status === "timeout"
                ? "Your payment is taking a little longer than usual. If you completed checkout, you'll get a confirmation email shortly."
                : "Your deposit wasn't completed, so the week wasn't reserved. You can try again anytime."}
            </p>
            <Button asChild className="btn-sweep bg-primary text-primary-foreground hover:text-primary-foreground rounded-sm h-12 px-8 mt-8">
              <Link to="/#order" data-testid="try-again-button">Try again</Link>
            </Button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
